import React, { useState, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import * as Icons from 'react-icons/fa';

const FaTimes = Icons.FaTimes as any;

interface FormHistory {
  uid: string;
  code?: string;
  status: string;
  Status?: string;
  modified_time?: string;
  modifiedTime?: string;
  modified_by?: string;
  modifiedBy?: string;
  user_name?: string;
}

interface RevertModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (historyUID: string, remarks: string, revertToStatus: string) => void;
  formHistory: FormHistory[];
  isLoading?: boolean;
}

const RevertModal: React.FC<RevertModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  formHistory,
  isLoading = false
}) => {
  const [selectedHistoryUID, setSelectedHistoryUID] = useState('');
  const [remarks, setRemarks] = useState('');
  const [error, setError] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      setSelectedHistoryUID('');
      setRemarks('');
      setError('');
      setIsDropdownOpen(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isLoading) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isDropdownOpen) {
          setIsDropdownOpen(true);
        } else {
          setHighlightedIndex(prev =>
            prev < formHistory.length - 1 ? prev + 1 : 0
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isDropdownOpen) {
          setHighlightedIndex(prev =>
            prev > 0 ? prev - 1 : formHistory.length - 1
          );
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (isDropdownOpen && highlightedIndex >= 0 && highlightedIndex < formHistory.length) {
          handleOptionSelect(formHistory[highlightedIndex]);
        } else if (!isDropdownOpen) {
          setIsDropdownOpen(true);
        }
        break;
      case 'Escape':
        setIsDropdownOpen(false);
        break;
    }
  };

  const handleSelectClick = () => {
    if (!isLoading) {
      setIsDropdownOpen(!isDropdownOpen);
    }
  };

  const handleOptionSelect = (item: FormHistory) => {
    const itemId = item.code || item.uid;
    setSelectedHistoryUID(itemId);
    setIsDropdownOpen(false);
    setHighlightedIndex(-1);
    setError('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedHistoryUID('');
    setIsDropdownOpen(false);
  };

  const getDisplayValue = () => {
    const selectedOption = formHistory.find(item => (item.code || item.uid) === selectedHistoryUID);
    if (!selectedOption) return '';
    
    const status = selectedOption.status || selectedOption.Status || '';
    const userName = selectedOption.user_name || '';
    const modifiedTime = selectedOption.modified_time || selectedOption.modifiedTime || '';
    return `${status}${userName ? ` - ${userName}` : ''}${modifiedTime ? ` (${new Date(modifiedTime).toLocaleString()})` : ''}`;
  };

  const handleConfirm = () => {
    // Validate history selection
    if (!selectedHistoryUID) {
      setError('Please select a status to revert to');
      return;
    }

    // Validate remarks
    if (!remarks || remarks.trim() === '') {
      setError('Remarks are mandatory for reverting the form');
      return;
    }

    // Get the selected history item
    const selectedHistory = formHistory.find(
      item => (item.code || item.uid) === selectedHistoryUID
    );

    if (!selectedHistory) {
      setError('Selected history not found');
      return;
    }

    // Extract and process the status
    const status = selectedHistory.status || selectedHistory.Status || '';
    let revertToStatus = status.includes('->') 
      ? status.split('->')[0].trim() 
      : status;

    // If it's a single word (no spaces), use "Draft" instead
    if (status.includes('->')) {
      if (revertToStatus && revertToStatus === 'Updated') {
        revertToStatus = 'Draft';
      }
    } else {
      revertToStatus = 'Draft';
    }

    onConfirm(selectedHistoryUID, remarks, revertToStatus);
  };

  if (!isOpen) return null;

  return (
    <>
      <Overlay onClick={onClose} />
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>Revert Form</ModalTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>

        <ModalBody>
          {error && <ErrorMessage>{error}</ErrorMessage>}

          <FormGroup>
            <Label>
              Select Status to Revert To <Required>*</Required>
            </Label>
            <SelectContainer ref={containerRef}>
              <Select
                type="text"
                value={getDisplayValue()}
                onClick={handleSelectClick}
                onKeyDown={handleKeyDown}
                placeholder="-- Select Status --"
                disabled={isLoading}
                readOnly
              />

              {selectedHistoryUID && !isLoading && (
                <ClearButton
                  type="button"
                  onClick={handleClear}
                  title="Clear selection"
                >
                  <FaTimes />
                </ClearButton>
              )}

              <DropdownList isOpen={isDropdownOpen}>
                {formHistory.length > 0 ? (
                  formHistory.map((item, index) => {
                    const displayStatus = item.status || item.Status || '';
                    const itemId = item.code || item.uid;
                    const userName = item.user_name || '';
                    const modifiedTime = item.modified_time || item.modifiedTime || '';

                    return (
                      <DropdownOption
                        key={itemId}
                        isSelected={itemId === selectedHistoryUID}
                        isHighlighted={index === highlightedIndex}
                        onClick={() => handleOptionSelect(item)}
                      >
                        {displayStatus}
                        {userName && ` - ${userName}`}
                        {modifiedTime && ` (${new Date(modifiedTime).toLocaleString()})`}
                      </DropdownOption>
                    );
                  })
                ) : (
                  <NoOptionsMessage>
                    No history available
                  </NoOptionsMessage>
                )}
              </DropdownList>
            </SelectContainer>
          </FormGroup>

          <FormGroup>
            <Label>
              Remarks <Required>*</Required>
            </Label>
            <TextArea
              value={remarks}
              onChange={(e) => {
                setRemarks(e.target.value);
                setError('');
              }}
              placeholder="Enter remarks for reverting the form..."
              rows={4}
              disabled={isLoading}
            />
          </FormGroup>
        </ModalBody>

        <ModalFooter>
          <CancelButton onClick={onClose} disabled={isLoading}>
            Cancel
          </CancelButton>
          <ConfirmButton onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? 'Reverting...' : 'Confirm Revert'}
          </ConfirmButton>
        </ModalFooter>
      </ModalContainer>
    </>
  );
};

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  z-index: 9998;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: ${props => props.theme.surface || 'white'};
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  width: 90%;
  max-width: 500px;
  z-index: 9999;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: scaleUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);

  @keyframes scaleUp {
    0% { 
      transform: translate(-50%, -50%) scale(0.3);
      opacity: 0;
    }
    100% { 
      transform: translate(-50%, -50%) scale(1);
      opacity: 1;
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  background: ${props => props.theme.primary || '#F40000'};
  color: white;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: white;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: white;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  overflow-y: auto;
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: ${props => props.theme.textPrimary || '#333'};
  font-size: 0.95rem;
`;

const Required = styled.span`
  color: ${props => props.theme.error || '#dc3545'};
  margin-left: 4px;
`;

const Select = styled.input`
  width: 100%;
  padding: 0.75rem;
  padding-right: 2.5rem;
  border: 1px solid ${props => props.theme.border || '#ddd'};
  border-radius: 8px;
  font-size: 0.95rem;
  background: white;
  transition: all 0.3s ease;
  color: ${props => props.theme.textPrimary || '#333'};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary || '#F40000'};
  }

  &:disabled {
    background-color: ${props => props.theme.surface || '#f5f5f5'};
    cursor: not-allowed;
    opacity: 0.6;
  }

  &.error {
    border-color: ${props => props.theme.error || '#dc3545'};
  }
`;

const SelectContainer = styled.div`
  position: relative;
  width: 100%;
`;

const ClearButton = styled.button`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${props => props.theme.textSecondary || '#999'};
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  opacity: 0.6;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
    color: ${props => props.theme.error || '#dc3545'};
  }

  &:disabled {
    display: none;
  }
`;

const DropdownList = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 200px;
  overflow-y: auto;
  background: white;
  border: 1px solid ${props => props.theme.border || '#ddd'};
  border-top: none;
  border-radius: 0 0 8px 8px;
  z-index: 1000;
  display: ${props => props.isOpen ? 'block' : 'none'};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const DropdownOption = styled.div<{ isSelected?: boolean; isHighlighted?: boolean }>`
  padding: 0.75rem;
  cursor: pointer;
  transition: background 0.2s;
  background: ${props => props.isSelected ? (props.theme.primary || '#F40000') + '10' :
               props.isHighlighted ? (props.theme.surface || '#f5f5f5') : 'white'};
  color: ${props => props.isSelected ? (props.theme.primary || '#F40000') : (props.theme.textPrimary || '#333')};
  font-weight: ${props => props.isSelected ? '600' : '400'};

  &:hover {
    background: ${props => props.theme.surface || '#f5f5f5'};
  }
`;

const NoOptionsMessage = styled.div`
  padding: 0.75rem;
  text-align: center;
  color: ${props => props.theme.textSecondary || '#999'};
  font-style: italic;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.border || '#ddd'};
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: inherit;
  resize: vertical;
  transition: all 0.3s ease;
  color: ${props => props.theme.textPrimary || '#333'};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary || '#F40000'};
    box-shadow: 0 0 0 3px ${props => props.theme.primary || '#F40000'}20;
  }

  &:disabled {
    background-color: ${props => props.theme.surface || '#f5f5f5'};
    cursor: not-allowed;
    opacity: 0.6;
  }

  &::placeholder {
    color: ${props => props.theme.textSecondary || '#999'};
  }
`;

const ErrorMessage = styled.div`
  background-color: ${props => props.theme.error || '#dc3545'}15;
  border: 1px solid ${props => props.theme.error || '#dc3545'};
  color: ${props => props.theme.error || '#dc3545'};
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 1.25rem;
  font-size: 0.9rem;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid ${props => props.theme.border || '#e0e0e0'};
  background-color: ${props => props.theme.surface || '#f9f9f9'};
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  min-width: 100px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background-color: ${props => props.theme.buttonSecondary || '#6c757d'};
  color: white;

  &:hover:not(:disabled) {
    background-color: ${props => props.theme.buttonSecondaryHover || '#5a6268'};
    transform: translateY(-2px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const ConfirmButton = styled(Button)`
  background: ${props => props.theme.primary || '#F40000'};
  color: white;

  &:hover:not(:disabled) {
    background: ${props => props.theme.primaryDark || '#B50000'};
    transform: translateY(-2px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

export default RevertModal;
